/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from "@tanstack/react-query";
import { refetchApiInterval } from "../../shared/constants";
import useGlobalStore from "../../store/useGlobalStore";
import { getAssetSocChartDetailsData, getMapDirectionData, getProjectAssetChargeActivitiesData, getProjectAssetChargeActivitiesDataWithAssetId, getProjectAssetsData, getProjectEnergyActivitiesData, getProjectEnergyMetricsData, getProjectListData, getProjectNetworkData, getUsageHistoryDetailsData } from "../api/project-asset-details.service";
import { getProjectAssetTrendsDetails } from "../api/power-trends-service";
export const useProjectListQuery = (queryParams, partyNumber) => {
    return useQuery({
        enabled: !!partyNumber,
        gcTime: 0,
        queryFn: () => {
            return getProjectListData(queryParams);
        },
        queryKey: ["projectList", queryParams],
        refetchInterval: refetchApiInterval,
        retry: 1
    });
};
export const useProjectAssetsDetailsQuery = (id, queryParams, refetchApiData) => {
    return useQuery({
        gcTime: 0,
        queryFn: () => {
            return getProjectAssetsData(id, queryParams);
        },
        queryKey: ["projectAssetsDetails", id, queryParams, refetchApiData],
        refetchInterval: refetchApiInterval,
        retry: 1
    });
};
export const useUsageHistoryDetailsQuery = ({ aggregation, assetId, assetSourceType, fetchHistoryData, id, refetchApiData, refreshTimestamp, startDate }) => {
    const setIsLoading = useGlobalStore((state) => state.setIsUsageHistoryLoading);
    return useQuery({
        enabled: fetchHistoryData,
        gcTime: 0,
        queryFn: async () => {
            setIsLoading(true);
            const response = await getUsageHistoryDetailsData(id, startDate, aggregation, assetSourceType, assetId);
            setIsLoading(false);
            if (!response) {
                throw new Error("No Data Available");
            }
            return response;
        },
        queryKey: [
            "UsageHistory",
            startDate,
            aggregation,
            id,
            assetId,
            assetSourceType,
            refreshTimestamp,
            refetchApiData
        ],
        refetchInterval: refetchApiInterval,
        refetchOnWindowFocus: false,
        retry: 1
    });
};
export const useProjectEnergyMetricsQuery = (id, startDate, refetchApiData) => {
    return useQuery({
        gcTime: 0,
        queryFn: async () => {
            return await getProjectEnergyMetricsData(id, startDate);
        },
        queryKey: ["EnergyMetrics", startDate, id, refetchApiData],
        refetchInterval: refetchApiInterval,
        refetchOnWindowFocus: false,
        retry: 1
    });
};
export const useProjectNetworkQuery = (id, refetchApiData) => {
    return useQuery({
        gcTime: 0,
        queryFn: async () => {
            return await getProjectNetworkData(id);
        },
        queryKey: ["projectNetwork", id, refetchApiData],
        refetchInterval: refetchApiInterval,
        retry: 1
    });
};
export const useProjectPowerSocTrendsQuery = (projectId, startDate, refetchApiData) => {
    return useQuery({
        gcTime: 0,
        queryFn: async () => {
            return await getProjectAssetTrendsDetails(projectId, startDate);
        },
        queryKey: ["projectPowerSocTrends", projectId, startDate, refetchApiData],
        refetchInterval: refetchApiInterval,
        retry: 1
    });
};
export const useAssetPowerSocTrendsQuery = (projectId, startDate, assetId, frequency, refetchApiData) => {
    return useQuery({
        gcTime: 0,
        queryFn: async () => {
            return await getProjectAssetTrendsDetails(projectId, startDate, assetId, frequency);
        },
        queryKey: ["assetPowerSocTrends", projectId, startDate, assetId, frequency, refetchApiData],
        refetchInterval: refetchApiInterval,
        retry: 1
    });
};
export const useProjectEnergyActivitiesQuery = (id, startDate, fetchHistoryData, refetchApiData) => {
    return useQuery({
        enabled: fetchHistoryData,
        gcTime: 0,
        queryFn: () => {
            return getProjectEnergyActivitiesData(id, startDate);
        },
        queryKey: ["EnergyActivities", startDate, id, refetchApiData],
        refetchInterval: refetchApiInterval,
        refetchOnWindowFocus: false,
        retry: 1
    });
};
export const useProjectAssetChargeActivitiesQuery = (id, startDate, assetId, fetchHistoryData, refreshTimestamp, refetchApiData) => {
    const setIsLoading = useGlobalStore((state) => state.setIsLoading);
    return useQuery({
        enabled: fetchHistoryData,
        gcTime: 0,
        queryFn: async () => {
            setIsLoading(true);
            let response;
            if (assetId) {
                response = await getProjectAssetChargeActivitiesDataWithAssetId(id, startDate, assetId);
            }
            else {
                response = await getProjectAssetChargeActivitiesData(id, startDate);
            }
            setIsLoading(false);
            // Check if response has assetChargeActivities array
            if (!response || !response.assetChargeActivities || response.assetChargeActivities.length === 0) {
                throw new Error("No Data Available");
            }
            return response;
        },
        queryKey: ["AssetChargeActivities", startDate, id, assetId, refreshTimestamp, refetchApiData],
        refetchInterval: refetchApiInterval,
        refetchOnWindowFocus: false,
        retry: 1
    });
};
export const useMapDirectionQuery = (requestBody, refetchApiData) => {
    return useQuery({
        gcTime: 0,
        queryFn: async () => {
            return await getMapDirectionData(requestBody);
        },
        queryKey: ["mapDirection", refetchApiData],
        refetchInterval: refetchApiInterval,
        retry: 1
    });
};
export const useAssetSocChartQuery = (assetId, fetchIdleBurnSoc, refetchApiData) => {
    return useQuery({
        enabled: fetchIdleBurnSoc,
        gcTime: 0,
        queryFn: () => {
            return getAssetSocChartDetailsData(assetId);
        },
        queryKey: ["assetSocChartDetails", assetId, refetchApiData],
        refetchInterval: refetchApiInterval,
        retry: 1
    });
};
